import { jsx, jsxs } from 'react/jsx-runtime';
import { isScrollable, canScrollTo, useSize, useResizeObserver } from '@flex-design-system/fx-utils';
import { styled } from '@flex-design-system/stitches-react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { Primitive } from '@radix-ui/react-primitive';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import classNames from 'classnames';
import { forwardRef, useState, useRef, useCallback } from 'react';
import { ScrollArea } from '../../../scroll-area/ScrollArea.mjs';
import { useModalContext } from '../../context.mjs';
import { StyledScrollAreaViewport, StyledScrollAreaChildrenRoot } from '../../styled.mjs';
import { FOG_HEIGHT, scrollAreaViewPortPaddingTop, scrollAreaViewPortPaddingBottom, scrollAreaViewPort } from '../../styles.vanilla.mjs';
import { ModalFog } from '../fog/Fog.mjs';
import { scrollAreaRoot, scrollbarMarginTop } from './scrollArea.vanilla.mjs';

const ModalScrollArea = forwardRef((props, ref) => {
  const {
    children,
    css,
    className,
    style: styleFromProps,
    toolbar,
    onScroll,
    toolbarHeightFallback = 0,
    bottomFog,
    ...bodyProps
  } = props;
  const [viewportElement, setViewportElement] = useState(
    null
  );
  const childrenRef = useRef(null);
  const {
    onContentElementChange,
    setScrollPos,
    toolbar: toolbarElement
  } = useModalContext("Modal.ScrollArea");
  const childrenComposedRef = useComposedRefs(
    childrenRef,
    onContentElementChange
  );
  const scrollable = isScrollable(viewportElement);
  const isFogDisabled = bottomFog === false;
  const isFogDisplayed = !isFogDisabled && canScrollTo(viewportElement, "bottom");
  const toolbarSize = useSize(toolbarElement ?? null);
  const toolbarElementHeight = toolbarSize?.height ?? 0;
  const toolbarHeight = toolbarElementHeight > 0 ? toolbarElementHeight : toolbarHeightFallback;
  const handleScroll = useCallback(() => {
    setScrollPos(viewportElement?.scrollTop ?? 0);
  }, [setScrollPos, viewportElement?.scrollTop]);
  useResizeObserver(childrenRef.current, handleScroll);
  return /* @__PURE__ */ jsx(
    StyledDiv,
    {
      className: scrollAreaRoot,
      style: Object.assign(
        {},
        assignInlineVars({ [scrollbarMarginTop]: `${toolbarHeight}px` }),
        styleFromProps
      ),
      ref,
      ...bodyProps,
      children: /* @__PURE__ */ jsxs(ScrollArea, { children: [
        /* @__PURE__ */ jsxs(
          StyledScrollAreaViewport,
          {
            css,
            className: classNames(scrollAreaViewPort, className),
            style: Object.assign(
              {},
              assignInlineVars({
                [scrollAreaViewPortPaddingBottom]: `${scrollable ? FOG_HEIGHT : 64}px`,
                [scrollAreaViewPortPaddingTop]: `${toolbarHeight}px`
              })
            ),
            ref: setViewportElement,
            onScroll: composeEventHandlers(onScroll, handleScroll),
            children: [
              toolbar,
              /* @__PURE__ */ jsx(StyledScrollAreaChildrenRoot, { ref: childrenComposedRef, children })
            ]
          }
        ),
        isFogDisplayed ? bottomFog ?? /* @__PURE__ */ jsx(ModalFog, { side: "bottom", color: "white" }) : null
      ] })
    }
  );
});
const StyledDiv = styled(Primitive.div);

export { ModalScrollArea };
//# sourceMappingURL=ScrollArea.mjs.map
